/**
 * Utilidades compartidas para el sistema de menciones
 * Consolidación de lógica común para evitar duplicación
 *
 * SIMPLIFICACIÓN: Usa delimitador explícito `|` para nombres de documentos
 */

import type { MentionType, ParsedMention } from '../components/chat/MentionTypes';

/**
 * Delimitador para nombres de documentos en menciones
 * Indica explícitamente el final del nombre del documento
 */
export const DOCUMENT_NAME_DELIMITER = '|';

/**
 * Regex para detectar menciones en el texto
 * Soporta: @tipo:valor o @valor (auto-detección)
 */
const MENTION_REGEX = /@(?:(\w+):)?([^\s@]+)/g;

/**
 * Regex simplificado para documentos: captura desde @documento: hasta el delimitador |
 * IMPORTANTE: Permite espacios cuando hay delimitador | al final
 * BACKWARD COMPATIBLE: Si no hay delimitador |, captura hasta el primer espacio (comportamiento antiguo)
 * MUCHO MÁS SIMPLE: No requiere regex complejo ni nombres conocidos
 */
// Primero intentar capturar con delimitador | (permite espacios)
const DOCUMENT_MENTION_WITH_DELIMITER = /@documento:([^|@]+)\|/g;
// Fallback: sin delimitador (compatibilidad hacia atrás, no permite espacios)
const DOCUMENT_MENTION_WITHOUT_DELIMITER = /@documento:([^|\s@]+)(?!\|)/g;

/**
 * Normaliza un valor de mención para comparación (case-insensitive, trimmed)
 */
export function normalizeMentionValue(value: string): string {
  return value.trim().toLowerCase();
}

/**
 * Extrae el tipo de mención de un texto
 */
export function getMentionType(mentionText: string): MentionType | 'auto' {
  const match = mentionText.match(/@(?:(\w+):)?/);
  if (!match || !match[1]) return 'auto';
  return match[1] as MentionType;
}

/**
 * Detecta si una mención está completa (terminada con espacio o al final del texto)
 */
export function isMentionComplete(
  mentionEndIndex: number,
  textLength: number,
  text: string,
  value: string
): boolean {
  if (!value || value.trim().length === 0) return false;
  const hasSpaceAfter = mentionEndIndex < textLength && /\s/.test(text[mentionEndIndex]);
  const isAtEnd = mentionEndIndex >= textLength;
  return hasSpaceAfter || isAtEnd;
}

/**
 * Detecta todas las menciones en un texto
 * SIMPLIFICADO: Usa delimitador explícito `|` para documentos
 * @param text - Texto a analizar
 * @param knownDocumentNames - Nombres exactos de documentos seleccionados (opcional, ya no necesario con delimitador)
 */
const _hasOverlap = (start: number, end: number, ranges: Array<{ start: number; end: number }>): boolean => {
  return ranges.some(range => {
    return (start >= range.start && start < range.end) ||
           (end > range.start && end <= range.end) ||
           (start <= range.start && end >= range.end);
  });
};

const _createDocumentMention = (fullMatch: string, value: string, startIndex: number): ParsedMention => {
  return {
    type: 'documento' as MentionType,
    value: value.trim(),
    fullText: fullMatch,
    startIndex,
    endIndex: startIndex + fullMatch.length,
  };
};

const _processDocumentWithDelimiter = (
  text: string,
  regex: RegExp,
  mentions: ParsedMention[],
  processedRanges: Array<{ start: number; end: number }>
): void => {
  let match;
  while ((match = regex.exec(text)) !== null) {
    const [fullMatch, value] = match;
    const trimmedValue = value.trim();
    if (!trimmedValue) continue;

    const startIndex = match.index!;
    const endIndex = startIndex + fullMatch.length;

    if (!_hasOverlap(startIndex, endIndex, processedRanges)) {
      processedRanges.push({ start: startIndex, end: endIndex });
      mentions.push(_createDocumentMention(fullMatch, trimmedValue, startIndex));
    }
  }
};

const _processDocumentWithoutDelimiter = (
  text: string,
  regex: RegExp,
  mentions: ParsedMention[],
  processedRanges: Array<{ start: number; end: number }>
): void => {
  let match;
  while ((match = regex.exec(text)) !== null) {
    const [fullMatch, value] = match;
    const trimmedValue = value.trim();
    if (!trimmedValue) continue;

    const startIndex = match.index!;
    const endIndex = startIndex + fullMatch.length;

    if (!_hasOverlap(startIndex, endIndex, processedRanges)) {
      processedRanges.push({ start: startIndex, end: endIndex });
      mentions.push(_createDocumentMention(fullMatch, trimmedValue, startIndex));
    }
  }
};

const _processOtherMentions = (
  text: string,
  regex: RegExp,
  mentions: ParsedMention[],
  processedRanges: Array<{ start: number; end: number }>
): void => {
  let match = null;
  while ((match = regex.exec(text)) !== null) {
    const [fullMatch, type, value] = match;
    if (type === 'documento') continue;

    const startIndex = match.index!;
    const endIndex = startIndex + fullMatch.length;

    if (_hasOverlap(startIndex, endIndex, processedRanges)) continue;

    mentions.push({
      type: (type as MentionType) || 'auto',
      value: value.trim(),
      fullText: fullMatch,
      startIndex,
      endIndex,
    });
  }
};

export function detectMentions(text: string, knownDocumentNames?: string[]): ParsedMention[] {
  const mentions: ParsedMention[] = [];
  const processedRanges: Array<{ start: number; end: number }> = [];

  const documentWithDelimiterRegex = new RegExp(DOCUMENT_MENTION_WITH_DELIMITER.source, DOCUMENT_MENTION_WITH_DELIMITER.flags);
  const documentWithoutDelimiterRegex = new RegExp(DOCUMENT_MENTION_WITHOUT_DELIMITER.source, DOCUMENT_MENTION_WITHOUT_DELIMITER.flags);
  const mentionRegex = new RegExp(MENTION_REGEX.source, MENTION_REGEX.flags);

  _processDocumentWithDelimiter(text, documentWithDelimiterRegex, mentions, processedRanges);
  _processDocumentWithoutDelimiter(text, documentWithoutDelimiterRegex, mentions, processedRanges);
  _processOtherMentions(text, mentionRegex, mentions, processedRanges);

  mentions.sort((a, b) => a.startIndex - b.startIndex);
  return mentions;
}

/**
 * Extrae información de una mención desde el texto en una posición específica
 * SIMPLIFICADO: Usa delimitador explícito para documentos
 */
export function extractMentionFromText(
  text: string,
  cursorPos: number
): { type: MentionType | 'auto'; value: string; startIndex: number; endIndex: number } | null {
  const textBeforeCursor = text.substring(0, cursorPos);
  const lastAtIndex = textBeforeCursor.lastIndexOf('@');

  if (lastAtIndex === -1) return null;

  const textAfterAt = text.substring(lastAtIndex);

  // Para documentos, primero intentar con delimitador | (permite espacios)
  let documentMatch = textAfterAt.match(/^@documento:([^|@]+)\|/);
  if (documentMatch) {
    const [fullMatch, value] = documentMatch;
    const trimmedValue = value.trim();
    return {
      type: 'documento' as MentionType,
      value: trimmedValue,
      startIndex: lastAtIndex,
      endIndex: lastAtIndex + fullMatch.length, // Incluye el delimitador |
    };
  }
  // Fallback: sin delimitador (compatibilidad hacia atrás, no permite espacios)
  documentMatch = textAfterAt.match(/^@documento:([^|\s@]+)(?!\|)/);
  if (documentMatch) {
    const [fullMatch, value] = documentMatch;
    const trimmedValue = value.trim();
    return {
      type: 'documento' as MentionType,
      value: trimmedValue,
      startIndex: lastAtIndex,
      endIndex: lastAtIndex + fullMatch.length,
    };
  }

  // Para otras menciones, usar el regex normal
  const match = textAfterAt.match(/^@(?:(\w+):)?([^\s@]*)/);
  if (!match) return null;

  const [fullMatch, type, value] = match;
  return {
    type: (type as MentionType) || 'auto',
    value: value.trim(),
    startIndex: lastAtIndex,
    endIndex: lastAtIndex + fullMatch.length,
  };
}

/**
 * Mapea un tipo de mención a la key correspondiente en MentionFilters
 * SIMPLIFICACIÓN: Solo 3 campos esenciales
 */
export function getFilterKeyForMentionType(type: MentionType | 'auto'): keyof import('../components/chat/MentionTypes').MentionFilters | null {
  switch (type) {
    case 'empresa':
      return 'company';
    case 'tipo':
      return 'document_type';
    case 'fecha':
      return 'document_period';
    case 'auto':
      // Auto-detección eliminada, pero mantener para compatibilidad temporal
      return 'company';
    default:
      return null;
  }
}

